// src/pages/customers/CustomerDetail.jsx
import React, { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, Tab, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import WrapperSection from "../../components/global/WrapperSection";
import GlobalBreadcrumbs from "../../components/global/GlobalBreadcrumbs";
import LoadingPage from "../../components/global/LoadingPage";

import { useGetCustomerById } from "../../hooks/customers/useGetCustomerById";

import CustomerHeader from "../../components/customers/Detail/CustomerHeader";
import CustomerStatsRow from "../../components/customers/Detail/CustomerStatsRow";
import CustomerProfileTab from "../../components/customers/Detail/CustomerProfileTab";
import CustomerOrdersTab from "../../components/customers/Detail/CustomerOrdersTab";
import CustomerAddressesTab from "../../components/customers/Detail/CustomerAddressesTab";
import CustomerWishlistTab from "../../components/customers/Detail/CustomerWishlistTab";
import CustomerRolesTab from "../../components/customers/Detail/CustomerRolesTab";

const TABS = ["profile", "orders", "addresses", "wishlist", "roles"];

const CustomerDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const [searchParams, setSearchParams] = useSearchParams();
    const initial = TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "profile";
    const [tab, setTab] = useState(initial);

    const { customer, isLoading, error } = useGetCustomerById(id);

    if (isLoading) return <LoadingPage />;
    if (error || !customer) {
        return (
            <WrapperSection>
                <GlobalBreadcrumbs />
                <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="h6" color="error">
                        {isArabic ? "تعذر تحميل بيانات العميل" : "Failed to load customer"}
                    </Typography>
                </Box>
            </WrapperSection>
        );
    }

    const handleTabChange = (_e, value) => {
        setTab(value);
        const next = new URLSearchParams(searchParams);
        next.set("tab", value);
        setSearchParams(next, { replace: true });
    };

    return (
        <WrapperSection>
            <GlobalBreadcrumbs />

            <CustomerHeader
                customer={customer}
                onDeleted={() => navigate("/customers")}
            />

            <CustomerStatsRow customer={customer} />

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab value="profile"   label={isArabic ? "الملف الشخصي" : "Profile"} />
                    <Tab value="orders"    label={isArabic ? "الطلبات" : "Orders"} />
                    <Tab value="addresses" label={isArabic ? "العناوين" : "Addresses"} />
                    <Tab value="wishlist"  label={isArabic ? "المفضلة" : "Wishlist"} />
                    <Tab value="roles"     label={isArabic ? "الأدوار" : "Roles"} />
                </Tabs>
            </Box>

            <Box>
                {tab === "profile"   && <CustomerProfileTab customer={customer} />}
                {tab === "orders"    && <CustomerOrdersTab customerId={customer.id} />}
                {tab === "addresses" && <CustomerAddressesTab customerId={customer.id} />}
                {tab === "wishlist"  && <CustomerWishlistTab customerId={customer.id} />}
                {tab === "roles"     && <CustomerRolesTab customerId={customer.id} />}
            </Box>
        </WrapperSection>
    );
};

export default CustomerDetail;
